import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Image, Text } from '@bonusly/design-system';

interface StackCardContentProps {
	title: string;
	description: string;
	imageUrl?: string;
	hasIncentive?: boolean;
	isFocused?: boolean;
	isTopCard?: boolean;
	isExpanded?: boolean;
}

/**
 * Simplified card content for the announcements stack.
 * Shows only title, description, optional image, and incentive link.
 */
export const StackCardContent: FC<StackCardContentProps> = ({
	title,
	description,
	imageUrl,
	hasIncentive,
	isFocused = false,
	isTopCard = false,
	isExpanded = false,
}) => {
	const { t } = useTranslation('announcements');

	return (
		<Box
			bg="bg.surface"
			px={4}
			py={3}
			borderRadius="md"
			border="1px solid"
			borderColor={isFocused ? 'primary.fg' : 'border.default'}
			boxShadow={isFocused ? 'outline' : 'xs'}
			width="100%"
			textAlign="left"
			display="flex"
			minH={24}
			alignItems="center"
			gap={6}
			transition="border-color 100ms ease, box-shadow 100ms ease"
			_hover={isExpanded ? { borderColor: 'border.strong' } : undefined}
			_groupHover={isTopCard && !isFocused ? { borderColor: 'border.strong' } : undefined}
		>
			<Box flex={1} display="flex" flexDirection="column" gap={0.5} minW={0}>
				<Text fontSize={16} fontWeight="semibold" noOfLines={1}>
					{title}
				</Text>
				<Text
					fontSize={14}
					color="text.soft"
					noOfLines={hasIncentive ? 1 : 2}
					sx={{
						'& *': {
							fontSize: 'inherit',
							fontWeight: 'normal',
							fontStyle: 'normal',
							color: 'inherit',
							margin: 0,
							padding: 0,
							display: 'inline',
						},
						'& p + p::before': {
							content: '" "',
						},
					}}
					dangerouslySetInnerHTML={{ __html: description }}
				/>
				{hasIncentive && (
					<Text as="span" color="text.interactive" fontSize="sm" fontWeight="medium">
						{t('carousel.incentive_button')}
					</Text>
				)}
			</Box>

			{/* Always reserve space for image to ensure consistent card heights */}
			<Box w={24} h={16} flexShrink={0} bg={imageUrl ? 'bg.subtle' : undefined} borderRadius="md">
				{imageUrl && <Image src={imageUrl} alt="" borderRadius="md" objectFit="cover" w="100%" h="100%" />}
			</Box>
		</Box>
	);
};
